import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, ArrowLeft, Clock } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" }
];

// Helper function to convert 24h time to 12h AM/PM format
const formatTimeTo12Hour = (time24) => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

export default function Availability() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [tech, setTech] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    day_of_week: 0,
    start_time: "09:00",
    end_time: "17:00"
  });

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAuthenticated(true);
          setUser(response.data);
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      } catch (error) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchTech();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (tech) {
      fetchAvailability();
    }
  }, [tech]);

  const fetchTech = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/techs`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      // Case-insensitive + whitespace-tolerant match. Google login returns
      // emails lowercased while admin-entered tech emails sometimes contain
      // capitals or trailing spaces — strict === was failing silently for
      // those techs and leaving them with no way to add availability.
      const normalize = (s) => (s || "").trim().toLowerCase();
      const myEmail = normalize(user.email);
      const myTech = response.data.find(t => normalize(t.email) === myEmail);
      if (!myTech) {
        console.error("No tech found for user email:", user.email, "— available:", response.data.map(t => t.email));
        toast.error(
          `No tech profile found for ${user.email}. Ask an admin to add you in Settings → Techs (make sure the email matches exactly).`,
          { duration: 12000 }
        );
        return;
      }
      setTech(myTech);
    } catch (error) {
      console.error("Failed to fetch tech:", error);
      toast.error("Failed to fetch tech info: " + (error.response?.data?.detail || error.message));
    }
  };

  const fetchAvailability = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/availability`, {
        params: tech ? { tech_id: tech.tech_id } : {},
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAvailability(response.data);
    } catch (error) {
      console.error("Failed to fetch availability:", error);
      toast.error("Failed to fetch availability: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!tech) {
      // Surface the silent dead-end that bit a new tech on prod —
      // previously we returned with no toast and the user just saw the
      // button click do nothing.
      toast.error(
        "Your tech profile isn't loaded yet. Refresh the page or ask an admin to add you in Settings → Techs.",
        { duration: 10000 }
      );
      return;
    }

    // Defensive validation — end_time must be after start_time, otherwise
    // the slot generator silently produces zero slots downstream.
    if (formData.end_time <= formData.start_time) {
      toast.error("End time must be after start time.");
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/availability`, {
        tech_id: tech.tech_id,
        ...formData
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Availability added");
      setShowAddForm(false);
      setFormData({
        day_of_week: 0,
        start_time: "09:00",
        end_time: "17:00"
      });
      fetchAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add availability");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/availability/${deleteId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Availability deleted");
      setDeleteId(null);
      fetchAvailability();
    } catch (error) {
      toast.error("Failed to delete availability");
    }
  };

  const handleToggleActive = async (avail) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/availability/${avail.availability_id}`, {
        is_active: !avail.is_active
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success(avail.is_active ? "Availability disabled" : "Availability enabled");
      fetchAvailability();
    } catch (error) {
      toast.error("Failed to update availability");
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header user={user} />
      
      <div className="max-w-md mx-auto pb-24 p-4">
        <div className="mb-6">
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-inter font-semibold">Back to Settings</span>
          </button>
          
          <h1 className="font-barlow text-4xl font-bold text-zinc-950 uppercase mb-2">My Availability</h1>
          <p className="font-inter text-zinc-600">Set your general weekly working hours</p>
        </div>

        {!showAddForm ? (
          <button
            onClick={() => setShowAddForm(true)}
            data-testid="add-availability-button"
            className="w-full h-12 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold flex items-center justify-center gap-2 mb-6"
          >
            <Plus className="w-5 h-5" />
            Add Availability
          </button>
        ) : (
          <form onSubmit={handleAdd} className="bg-white rounded-xl border-2 border-zinc-100 p-5 mb-6">
            <h3 className="font-inter text-lg font-bold text-zinc-900 mb-4">Add New Availability</h3>
            
            <div className="space-y-4">
              <div>
                <label className="font-inter font-semibold text-zinc-700 block mb-2">Day of Week</label>
                <select
                  value={formData.day_of_week}
                  onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })}
                  className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white"
                >
                  {DAYS_OF_WEEK.map(day => (
                    <option key={day.value} value={day.value}>{day.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-inter font-semibold text-zinc-700 block mb-2">Start Time</label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white"
                  />
                </div>
                <div>
                  <label className="font-inter font-semibold text-zinc-700 block mb-2">End Time</label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="h-12 flex-1 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 font-semibold hover:bg-zinc-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="h-12 flex-1 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold"
                >
                  Add
                </button>
              </div>
            </div>
          </form>
        )}

        <div className="space-y-4">
          {availability.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="font-inter text-zinc-500">No availability set yet</p>
              <p className="font-inter text-sm text-zinc-400 mt-1">Add your weekly working hours to get started</p>
            </div>
          ) : (
            availability.map((avail) => (
              <div
                key={avail.availability_id}
                className={`bg-white rounded-xl border-2 p-5 ${avail.is_active ? 'border-zinc-100' : 'border-zinc-200 opacity-50'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">
                      {DAYS_OF_WEEK.find(d => d.value === avail.day_of_week)?.label}
                    </h3>
                    <p className="text-zinc-600 mt-1">
                      {formatTimeTo12Hour(avail.start_time)} - {formatTimeTo12Hour(avail.end_time)}
                    </p>
                    <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-semibold ${avail.is_active ? 'bg-green-100 text-green-800' : 'bg-zinc-100 text-zinc-600'}`}>
                      {avail.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggleActive(avail)}
                      className="h-10 px-4 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 font-semibold hover:bg-zinc-50"
                    >
                      {avail.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => setDeleteId(avail.availability_id)}
                      className="h-10 w-10 rounded-lg bg-red-50 border-2 border-red-200 text-red-700 hover:bg-red-100 flex items-center justify-center"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Availability</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this availability slot? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
